import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ConfirmationService, MessageService, TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { forkJoin } from 'rxjs';
import {
  GainFormulaTemplate,
  GainFormulaTemplateType,
} from '../../../models/gain-formula-template.model';
import { GainParameter } from '../../../models/gain-parameter.model';
import { GainFormulaTemplateService } from '../../../services/gain-formula-template.service';
import { GainParameterService } from '../../../services/gain-parameter.service';
import {
  GainFormulaValidation,
  validateGainFormula,
} from './gain-formula.validator';

const CONFIRM_KEY = 'gain-templates';

type EditorMode = 'none' | 'new' | 'edit';

@Component({
  selector: 'app-gain-formula-templates',
  templateUrl: './gain-formula-templates.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GainFormulaTemplatesComponent implements OnInit {
  @ViewChild('equationInput') equationInput?: ElementRef<HTMLTextAreaElement>;

  readonly confirmKey = CONFIRM_KEY;

  types: GainFormulaTemplateType[] = [];
  templates: GainFormulaTemplate[] = [];
  parameters: GainParameter[] = [];
  parameterNames = new Set<string>();

  nodes: TreeNode[] = [];
  selectedNode: TreeNode | null = null;
  private expandedKeys = new Set<string>();

  showDeleted = false;
  loading = false;
  saving = false;

  mode: EditorMode = 'none';
  currentType: GainFormulaTemplateType | null = null;
  versions: GainFormulaTemplate[] = [];
  viewed: GainFormulaTemplate | null = null;
  form = { name: '', description: '', equation: '' };
  validation: GainFormulaValidation = {
    errors: [],
    references: [],
    unknownReferences: [],
  };

  typeDialogVisible = false;
  typeDialogMode: 'create' | 'rename' = 'create';
  typeDialogParentFK: string | null = null;
  typeDialogUid: string | null = null;
  typeForm = { name: '', description: '' };

  constructor(
    private templateService: GainFormulaTemplateService,
    private parameterService: GainParameterService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  // ---- Derived state ----

  get readOnly(): boolean {
    return (
      this.mode === 'edit' &&
      !!this.viewed &&
      (this.viewed.isDeleted ||
        this.viewed.version !== this.viewed.latestVersion)
    );
  }

  get versionOptions(): { label: string; value: string }[] {
    return this.versions.map((v) => ({
      label: `Version ${v.version}${v.version === v.latestVersion ? ' (courante)' : ''}`,
      value: v.uid,
    }));
  }

  get hasChanges(): boolean {
    if (this.mode === 'new') return true;
    const viewed = this.viewed;
    if (!viewed) return false;
    return (
      this.form.name.trim() !== viewed.name ||
      (this.form.description.trim() || null) !== (viewed.description ?? null) ||
      this.form.equation.trim() !== viewed.equation
    );
  }

  get canSave(): boolean {
    return (
      !this.readOnly &&
      !this.saving &&
      this.mode !== 'none' &&
      !!this.form.name.trim() &&
      this.validation.errors.length === 0 &&
      this.hasChanges
    );
  }

  get canDeleteTemplate(): boolean {
    return this.mode === 'edit' && !!this.viewed && !this.viewed.isDeleted;
  }

  get canSaveType(): boolean {
    return !!this.typeForm.name.trim() && !this.saving;
  }

  hasActiveTemplates(typeUid: string): boolean {
    return this.templates.some((t) => t.typeFK === typeUid && !t.isDeleted);
  }

  // ---- Loading / tree ----

  loadAll(afterLoad?: () => void): void {
    this.loading = true;
    forkJoin({
      types: this.templateService.getTypes(),
      templates: this.templateService.getTemplates(this.showDeleted),
      parameters: this.parameterService.getAll(false),
    }).subscribe({
      next: ({ types, templates, parameters }) => {
        this.types = types ?? [];
        this.templates = templates ?? [];
        this.parameters = parameters ?? [];
        this.parameterNames = new Set(this.parameters.map((p) => p.name));
        this.rebuildTree();
        this.loading = false;
        afterLoad?.();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        this.showError(err);
      },
    });
  }

  onShowDeletedChange(checked: boolean): void {
    this.showDeleted = checked;
    const groupId = this.mode === 'edit' ? this.viewed?.groupId : undefined;
    this.loadAll(() => this.reselect(groupId ? `tpl:${groupId}` : undefined));
  }

  onNodeExpand(node: TreeNode): void {
    if (node.key) this.expandedKeys.add(node.key);
  }

  onNodeCollapse(node: TreeNode): void {
    if (node.key) this.expandedKeys.delete(node.key);
  }

  private rebuildTree(): void {
    const childrenByParent = new Map<
      string | null,
      GainFormulaTemplateType[]
    >();
    for (const type of this.types) {
      const siblings = childrenByParent.get(type.parentFK) ?? [];
      siblings.push(type);
      childrenByParent.set(type.parentFK, siblings);
    }

    const build = (parent: string | null): TreeNode[] =>
      (childrenByParent.get(parent) ?? []).map((type) => {
        const childTypes = childrenByParent.get(type.uid) ?? [];
        const children =
          childTypes.length > 0
            ? build(type.uid)
            : this.templates
                .filter((t) => t.typeFK === type.uid)
                .map((t) => this.toTemplateNode(t));

        return {
          key: type.uid,
          label: type.name,
          expanded: this.expandedKeys.has(type.uid),
          data: { kind: 'type', type, isLeaf: childTypes.length === 0 },
          children,
        } as TreeNode;
      });

    this.nodes = build(null);
  }

  private toTemplateNode(template: GainFormulaTemplate): TreeNode {
    return {
      key: `tpl:${template.groupId}`,
      label: template.name,
      leaf: true,
      data: { kind: 'template', template },
    };
  }

  private findNode(
    key: string,
    nodes: TreeNode[] = this.nodes,
  ): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) return node;
      const found = node.children ? this.findNode(key, node.children) : null;
      if (found) return found;
    }
    return null;
  }

  private reselect(key?: string): void {
    const node = key ? this.findNode(key) : null;
    if (!node) {
      this.selectedNode = null;
      this.resetEditor();
      return;
    }

    this.selectedNode = node;
    if (node.data.kind === 'type') {
      this.selectType(node.data.type, node.data.isLeaf);
      return;
    }

    this.expandedKeys.add(node.data.template.typeFK);
    this.rebuildTree();
    this.selectedNode = this.findNode(key!);
    this.openTemplate(node.data.template);
  }

  // ---- Selection ----

  onNodeSelect(event: TreeNodeSelectEvent): void {
    const node = event.node;
    if (node.data.kind === 'type') {
      this.selectType(node.data.type, node.data.isLeaf);
    } else {
      this.openTemplate(node.data.template);
    }
  }

  onNodeUnselect(): void {
    this.resetEditor();
  }

  private selectType(type: GainFormulaTemplateType, isLeaf: boolean): void {
    if (!isLeaf) {
      this.resetEditor();
      return;
    }

    this.mode = 'new';
    this.currentType = type;
    this.versions = [];
    this.viewed = null;
    this.form = { name: '', description: '', equation: '' };
    this.onFormChange();
  }

  private openTemplate(template: GainFormulaTemplate): void {
    this.mode = 'edit';
    this.currentType =
      this.types.find((t) => t.uid === template.typeFK) ?? null;
    this.versions = [template];
    this.viewed = template;
    this.fillForm(template);

    this.templateService.getVersions(template.groupId).subscribe({
      next: (versions) => {
        if (this.viewed?.groupId !== template.groupId) return;
        this.versions = versions;
        this.cdr.markForCheck();
      },
      error: (err) => this.showError(err),
    });
  }

  onVersionChange(uid: string): void {
    const version = this.versions.find((v) => v.uid === uid);
    if (!version) return;
    this.viewed = version;
    this.fillForm(version);
  }

  private fillForm(template: GainFormulaTemplate): void {
    this.form = {
      name: template.name,
      description: template.description ?? '',
      equation: template.equation,
    };
    this.onFormChange();
  }

  private resetEditor(): void {
    this.mode = 'none';
    this.currentType = null;
    this.versions = [];
    this.viewed = null;
    this.form = { name: '', description: '', equation: '' };
    this.onFormChange();
  }

  // ---- Formula editing ----

  onFormChange(): void {
    this.validation = validateGainFormula(
      this.form.equation,
      this.parameterNames,
    );
  }

  onEquationChange(value: string): void {
    this.form.equation = value;
    this.onFormChange();
  }

  insertParameter(name: string): void {
    if (this.readOnly || this.mode === 'none') return;

    const textarea = this.equationInput?.nativeElement;
    const text = `[${name}]`;
    const current = this.form.equation;
    const start = textarea?.selectionStart ?? current.length;
    const end = textarea?.selectionEnd ?? start;

    this.onEquationChange(current.slice(0, start) + text + current.slice(end));

    // ngModel writes the new value asynchronously; restore the cursor afterwards.
    const position = start + text.length;
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(position, position);
    });
  }

  // ---- Template save / delete ----

  saveTemplate(): void {
    if (!this.canSave) return;

    const name = this.form.name.trim();
    const description = this.form.description.trim() || null;
    const equation = this.form.equation.trim();

    this.saving = true;
    const request$ =
      this.mode === 'new'
        ? this.templateService.createTemplate({
            typeFK: this.currentType!.uid,
            name,
            description,
            equation,
          })
        : this.templateService.updateTemplate({
            uid: this.viewed!.uid,
            name,
            description,
            equation,
          });

    request$.subscribe({
      next: (saved) => {
        this.saving = false;
        this.loadAll(() => this.reselect(`tpl:${saved.groupId}`));
      },
      error: (err) => {
        this.saving = false;
        this.showError(err);
      },
    });
  }

  confirmDeleteTemplate(): void {
    const template = this.viewed;
    if (!template) return;

    this.confirmationService.confirm({
      key: CONFIRM_KEY,
      message: `Êtes-vous sûr de vouloir supprimer le modèle « ${template.name} » ? Toutes ses versions seront masquées.`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.saving = true;
        this.templateService.deleteTemplate(template.groupId).subscribe({
          next: () => {
            this.saving = false;
            this.selectedNode = null;
            this.resetEditor();
            this.loadAll();
          },
          error: (err) => {
            this.saving = false;
            this.showError(err);
          },
        });
      },
    });
  }

  // ---- Type tree actions ----

  openCreateType(parentFK: string | null): void {
    this.typeDialogMode = 'create';
    this.typeDialogParentFK = parentFK;
    this.typeDialogUid = null;
    this.typeForm = { name: '', description: '' };
    this.typeDialogVisible = true;
  }

  openRenameType(type: GainFormulaTemplateType): void {
    this.typeDialogMode = 'rename';
    this.typeDialogParentFK = type.parentFK;
    this.typeDialogUid = type.uid;
    this.typeForm = { name: type.name, description: type.description ?? '' };
    this.typeDialogVisible = true;
  }

  saveType(): void {
    if (!this.canSaveType) return;

    const name = this.typeForm.name.trim();
    const description = this.typeForm.description.trim() || null;
    const parentFK = this.typeDialogParentFK;

    this.saving = true;
    const request$ =
      this.typeDialogMode === 'create'
        ? this.templateService.createType({ parentFK, name, description })
        : this.templateService.updateType({
            uid: this.typeDialogUid!,
            name,
            description,
          });

    request$.subscribe({
      next: () => {
        this.saving = false;
        this.typeDialogVisible = false;
        if (parentFK) this.expandedKeys.add(parentFK);

        const keepKey = this.selectedNode?.key;
        this.loadAll(() => this.reselect(keepKey));
      },
      error: (err) => {
        this.saving = false;
        this.showError(err);
      },
    });
  }

  confirmDeleteType(type: GainFormulaTemplateType): void {
    this.templateService.getTypeDeletionImpact(type.uid).subscribe({
      next: (impact) => {
        if (impact.isBlocked) {
          this.messageService.add({
            key: 'globalToast',
            severity: 'error',
            summary: 'Suppression impossible',
            detail: `Des modèles de cette branche sont utilisés par des règles : ${impact.blockingTemplates.join(', ')}.`,
          });
          return;
        }

        const extras: string[] = [];
        extras.push(`${impact.descendantCount} sous-type(s)`);
        extras.push(`${impact.templateCount} modèle(s) de formule`);
        if (impact.deletedTemplateCount > 0) {
          extras.push(
            `${impact.deletedTemplateCount} modèle(s) déjà supprimé(s)`,
          );
        }

        this.confirmationService.confirm({
          key: CONFIRM_KEY,
          message: `Supprimer le type « ${type.name} » entraînera la suppression définitive de : ${extras.join(', ')} (toutes versions). Cette action est irréversible.`,
          header: 'Avertissement : suppression d’un type',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Supprimer',
          rejectLabel: 'Annuler',
          acceptButtonStyleClass: 'p-button-danger',
          accept: () => this.deleteType(type),
        });
        this.cdr.markForCheck();
      },
      error: (err) => this.showError(err),
    });
  }

  private deleteType(type: GainFormulaTemplateType): void {
    this.saving = true;
    this.templateService.deleteType(type.uid).subscribe({
      next: () => {
        this.saving = false;
        this.selectedNode = null;
        this.resetEditor();
        this.loadAll();
      },
      error: (err) => {
        this.saving = false;
        this.showError(err);
      },
    });
  }

  // ---- Errors ----

  private showError(err: any): void {
    this.messageService.add({
      key: 'globalToast',
      severity: 'error',
      summary: 'Erreur',
      detail: this.extractMessage(err),
    });
    this.cdr.markForCheck();
  }

  private extractMessage(err: any): string {
    const body = err?.body;
    if (typeof body === 'string' && body) {
      try {
        const parsed = JSON.parse(body);
        return parsed?.message ?? parsed?.detail ?? parsed?.title ?? body;
      } catch {
        return body;
      }
    }
    return err?.message ?? "Une erreur est survenue lors de l'opération.";
  }
}
